/**
import logger from '../utils/logger';
 * Test Page for Background Tasks
 * Manual testing interface for background sync functionality
 * Navigate to /test-background-tasks to use this page
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import backgroundTaskManager from '../utils/backgroundTasks';
import pixleyDB from '../utils/db';
import { showToast } from '../components/Toast';
import logger from '../utils/logger';

const TestBackgroundTasks = () => {
  const { user } = useAuth();
  const [dbStats, setDbStats] = useState(null);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [isSupported, setIsSupported] = useState(false);
  const [testResults, setTestResults] = useState([]);

  useEffect(() => {
    checkSupport();
    loadStats();
    loadPendingTasks();

    const interval = setInterval(() => {
      loadStats();
      loadPendingTasks();
    }, 3000);

    return () => clearInterval(interval);
  }, [user?.id]);

  const checkSupport = () => {
    const supported = backgroundTaskManager.isBackgroundSyncSupported();
    setIsSupported(supported);
    addTestResult(supported ? '✅ Background Sync Supported' : '❌ Background Sync Not Supported');
  };

  const loadStats = async () => {
    try {
      const stats = await pixleyDB.getStats();
      setDbStats(stats);
    } catch (error) {
      logger.error('Error loading stats:', error);
    }
  };

  const loadPendingTasks = async () => {
    if (!user?.id) return;
    try {
      const tasks = await pixleyDB.getPendingTasksForUser(user.id);
      setPendingTasks(tasks);
    } catch (error) {
      logger.error('Error loading pending tasks:', error);
    }
  };

  const addTestResult = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setTestResults(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const testIndexedDB = async () => {
    try {
      addTestResult('🔍 Testing IndexedDB...');

      await pixleyDB.init();
      addTestResult('✅ IndexedDB initialized');

      const testTask = {
        id: `test-${Date.now()}`,
        type: 'character_creation',
        userId: user?.id || 'test-user',
        data: { name: 'Test Character' },
        status: 'pending'
      };

      await pixleyDB.storePendingTask(testTask);
      addTestResult('✅ Test task stored');

      const retrieved = await pixleyDB.getPendingTask(testTask.id);
      if (retrieved) {
        addTestResult('✅ Test task retrieved');
      } else {
        addTestResult('❌ Test task not found');
      }

      await pixleyDB.deleteTask(testTask.id);
      addTestResult('✅ Test task deleted');

      loadStats();
      loadPendingTasks();

    } catch (error) {
      addTestResult(`❌ IndexedDB test failed: ${error.message}`);
    }
  };

  const testServiceWorkerRegistration = async () => {
    try {
      addTestResult('🔍 Testing Service Worker...');

      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.register('/sw.js');
        addTestResult('✅ Service Worker registered');

        if ('sync' in registration) {
          const testSyncTag = `test-sync-${Date.now()}`;
          await registration.sync.register(testSyncTag);
          addTestResult(`✅ Background sync registered: ${testSyncTag}`);
        } else {
          addTestResult('❌ Background sync not available');
        }
      } else {
        addTestResult('❌ Service Worker not supported');
      }
    } catch (error) {
      addTestResult(`❌ Service Worker test failed: ${error.message}`);
    }
  };

  const testBackgroundCharacterCreation = async () => {
    if (!user?.id) {
      addTestResult('❌ User not authenticated');
      return;
    }

    try {
      addTestResult('🎨 Testing background character creation...');

      // Create a test canvas and convert to blob
      const canvas = document.createElement('canvas');
      canvas.width = 100;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');

      // Draw a simple test image
      ctx.fillStyle = '#ff0000';
      ctx.fillRect(0, 0, 50, 50);
      ctx.fillStyle = '#00ff00';
      ctx.fillRect(50, 0, 50, 50);
      ctx.fillStyle = '#0000ff';
      ctx.fillRect(0, 50, 50, 50);
      ctx.fillStyle = '#ffff00';
      ctx.fillRect(50, 50, 50, 50);

      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/png', 1.0);
      });

      const characterData = {
        user_id: user.id,
        name: `Test Character ${Date.now()}`,
        handdrawn_image: blob,
        animated_image: blob
      };

      const taskId = await backgroundTaskManager.createCharacterInBackground(characterData);
      addTestResult(`✅ Background task created: ${taskId}`);

      // Set up listener
      backgroundTaskManager.addTaskListener(taskId, (result) => {
        if (result.status === 'completed') {
          addTestResult(`✅ Character creation completed: ${taskId}`);
        } else if (result.status === 'failed') {
          addTestResult(`❌ Character creation failed: ${result.error}`);
        }
      });

      loadPendingTasks();

    } catch (error) {
      addTestResult(`❌ Background character creation test failed: ${error.message}`);
    }
  };

  const requestNotificationPermission = async () => {
    try {
      addTestResult('🔔 Requesting notification permission...');

      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        addTestResult(`📢 Notification permission: ${permission}`);

        if (permission === 'granted') {
          // Test notification
          new Notification('Test Notification', {
            body: 'Background tasks system is working!',
            icon: '/favicon.ico'
          });
          addTestResult('✅ Test notification sent');
        }
      } else {
        addTestResult('❌ Notifications not supported');
      }
    } catch (error) {
      addTestResult(`❌ Notification test failed: ${error.message}`);
    }
  };

  const cleanupOldTasks = async () => {
    try {
      addTestResult('🧹 Cleaning up old tasks...');
      const cleaned = await pixleyDB.cleanupOldTasks(60000); // 1 minute for testing
      addTestResult(`✅ Cleaned up ${cleaned} old tasks`);
      loadStats();
      loadPendingTasks();
    } catch (error) {
      addTestResult(`❌ Cleanup failed: ${error.message}`);
    }
  };

  if (!user) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">Background Tasks Test</h1>
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          Please log in to test background tasks functionality.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Background Tasks Test</h1>

      {/* Support Status */}
      <div className="mb-6 p-4 border rounded">
        <h2 className="text-lg font-semibold mb-2">Support Status</h2>
        <div className={`p-2 rounded ${isSupported ? 'bg-green-100' : 'bg-red-100'}`}>
          {isSupported ? '✅ Background Sync Supported' : '❌ Background Sync Not Supported'}
        </div>
        <div className="mt-2 text-sm text-gray-600">
          User: {user?.email || 'Unknown'} (ID: {user?.id || 'Unknown'})
        </div>
      </div>

      {/* Database Stats */}
      <div className="mb-6 p-4 border rounded">
        <h2 className="text-lg font-semibold mb-2">Database Stats</h2>
        {dbStats ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div>Total: {dbStats.total}</div>
            <div>Pending: {dbStats.pending}</div>
            <div>Processing: {dbStats.processing}</div>
            <div>Completed: {dbStats.completed}</div>
            <div>Failed: {dbStats.failed}</div>
          </div>
        ) : (
          <div>Loading...</div>
        )}
      </div>

      {/* Pending Tasks */}
      <div className="mb-6 p-4 border rounded">
        <h2 className="text-lg font-semibold mb-2">Pending Tasks ({pendingTasks.length})</h2>
        <div className="max-h-40 overflow-y-auto">
          {pendingTasks.length === 0 ? (
            <div className="text-gray-500 text-sm">No pending tasks</div>
          ) : (
            pendingTasks.map(task => (
              <div key={task.id} className="p-2 border-b text-sm">
                <div><strong>{task.type}</strong> - {task.status}</div>
                <div className="text-gray-600">{task.data.name || 'Unnamed'}</div>
                <div className="text-xs text-gray-500">{task.id}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Test Buttons */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={testIndexedDB}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Test IndexedDB
        </button>
        <button
          onClick={testServiceWorkerRegistration}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          Test Service Worker
        </button>
        <button
          onClick={testBackgroundCharacterCreation}
          className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
          disabled={!isSupported}
        >
          Test Character Creation
        </button>
        <button
          onClick={requestNotificationPermission}
          className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
        >
          Test Notifications
        </button>
        <button
          onClick={cleanupOldTasks}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Cleanup Old Tasks
        </button>
        <button
          onClick={() => setTestResults([])}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Clear Results
        </button>
      </div>

      {/* Test Results */}
      <div className="p-4 border rounded">
        <h2 className="text-lg font-semibold mb-2">Test Results</h2>
        <div className="bg-black text-green-400 p-4 rounded font-mono text-sm max-h-60 overflow-y-auto">
          {testResults.length === 0 ? (
            <div>No test results yet. Click buttons above to run tests.</div>
          ) : (
            testResults.map((result, index) => (
              <div key={index}>{result}</div>
            ))
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 p-4 bg-gray-100 rounded">
        <h3 className="font-semibold mb-2">Testing Instructions:</h3>
        <ol className="list-decimal list-inside text-sm space-y-1">
          <li>First run "Test IndexedDB" to verify database functionality</li>
          <li>Run "Test Service Worker" to verify service worker registration</li>
          <li>Run "Test Notifications" to enable notifications (click Allow when prompted)</li>
          <li>Run "Test Character Creation" to test the full background sync flow</li>
          <li>Navigate away from this page - the task should continue processing</li>
          <li>Come back to see if the task completed (check Pending Tasks section)</li>
          <li>You should receive a notification when the task completes</li>
        </ol>
      </div>
    </div>
  );
};

export default TestBackgroundTasks;